import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient, AdminResponse } from '../lib/api'

interface AuthContextType {
  user: AdminResponse | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user

  useEffect(() => {
    // Check if user is already authenticated on app start
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('admin_token')
        if (token) {
          const currentUser = await apiClient.getCurrentAdmin()
          setUser(currentUser)
        }
      } catch (error) {
        // If token is invalid, clear it
        localStorage.removeItem('admin_token')
        apiClient.clearToken()
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.login({ email, password })
      setUser(response.admin as AdminResponse)
      
      // Fetch complete user data to ensure all fields are populated
      try {
        const currentUser = await apiClient.getCurrentAdmin()
        setUser(currentUser)
      } catch (error) {
        // If fetching current user fails, keep the login response data
        console.warn('Failed to fetch complete user data:', error)
      }
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    apiClient.logout()
    setUser(null)
  }

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
